import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Mail, Phone, MapPin, Linkedin, Twitter, Github } from 'lucide-react';
import { toast } from 'sonner';
import apiService from '../services/api';
import { validateContact, createResolver, HONEYPOT_FIELD } from '../shared/validation';

const defaultValues = { name: '', email: '', message: '', [HONEYPOT_FIELD]: '' };
const resolver = createResolver(validateContact);

const Contact = () => {
  const [contactInfo, setContactInfo] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver,
    defaultValues,
    mode: 'onTouched'
  });

  useEffect(() => {
    const fetchContactInfo = async () => {
      const data = await apiService.getContactInfo();
      setContactInfo(data);
    };
    fetchContactInfo();
  }, []);

  // react-hook-form blocks re-entry while isSubmitting, so a double click
  // cannot produce a second request.
  const onSubmit = async (values) => {
    try {
      const response = await apiService.submitContactForm(values);
      toast.success(response.message);
      reset(defaultValues);
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (!contactInfo) return null;

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="max-w-3xl mb-16">
          <h2 className="text-5xl lg:text-6xl font-light tracking-tight text-gray-900 mb-6">
            Get in Touch
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Ready to start your project? Let's discuss how we can help you achieve your goals.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Form */}
          <div>
            <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-6">
              {/* Honeypot: hidden from people, irresistible to bots */}
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="hidden"
                {...register(HONEYPOT_FIELD)}
              />

              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  type="text"
                  disabled={isSubmitting}
                  className="mt-2"
                  placeholder="Your name"
                  aria-invalid={Boolean(errors.name)}
                  {...register('name')}
                />
                {errors.name && <p className="mt-1.5 text-sm text-red-600">{errors.name.message}</p>}
              </div>

              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  disabled={isSubmitting}
                  className="mt-2"
                  placeholder="your.email@example.com"
                  aria-invalid={Boolean(errors.email)}
                  {...register('email')}
                />
                {errors.email && <p className="mt-1.5 text-sm text-red-600">{errors.email.message}</p>}
              </div>

              <div>
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  disabled={isSubmitting}
                  className="mt-2 min-h-[150px]"
                  placeholder="Tell us about your project..."
                  aria-invalid={Boolean(errors.message)}
                  {...register('message')}
                />
                {errors.message && <p className="mt-1.5 text-sm text-red-600">{errors.message.message}</p>}
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-black text-white hover:bg-gray-800 transition-colors duration-200"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="space-y-8">
            {/* Email */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 text-gray-900" />
              </div>
              <div>
                <h3 className="font-normal text-gray-900 mb-1">Email</h3>
                <a
                  href={`mailto:${contactInfo.email}`}
                  className="text-gray-600 hover:text-gray-900 transition-colors"
                >
                  {contactInfo.email}
                </a>
              </div>
            </div>

            {/* Phone */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Phone className="w-6 h-6 text-gray-900" />
              </div>
              <div>
                <h3 className="font-normal text-gray-900 mb-1">Phone</h3>
                <a
                  href={`tel:${contactInfo.phone}`}
                  className="text-gray-600 hover:text-gray-900 transition-colors"
                >
                  {contactInfo.phone}
                </a>
              </div>
            </div>

            {/* Address */}
            {/* <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 text-gray-900" />
              </div>
              <div>
                <h3 className="font-normal text-gray-900 mb-1">Address</h3>
                <p className="text-gray-600">{contactInfo.address}</p>
              </div>
            </div> */}

            {/* Social Links */}
            <div className="pt-8 border-t border-gray-200">
              <h3 className="font-normal text-gray-900 mb-4">Follow Us</h3>
              <div className="flex gap-4">
                <a
                  href={contactInfo.social.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg flex items-center justify-center hover:scale-110 transition-transform"
                >
                  <Linkedin className="w-5 h-5 text-gray-900" />
                </a>
                <a
                  href={contactInfo.social.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg flex items-center justify-center hover:scale-110 transition-transform"
                >
                  <Twitter className="w-5 h-5 text-gray-900" />
                </a>
                <a
                  href={contactInfo.social.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg flex items-center justify-center hover:scale-110 transition-transform"
                >
                  <Github className="w-5 h-5 text-gray-900" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;